import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { subscribeNotifications } from '@seliseblocks/notifications';
import { GetNotificationsParams, Notification } from '../types/notification.types';
import {
  getNotifications,
  markAllNotificationAsRead,
  markNotificationAsRead,
} from '../services/notification.service';
import { useAuthStore } from '@/state/store/auth';

const projectKey = import.meta.env.VITE_X_BLOCKS_KEY || '';
const baseUrl = import.meta.env.VITE_API_BASE_URL || '';

// Blocks serves both the notification channel configs (`/Notification/Gets`) and the SignalR
// hub (`/NotificationHub`) under `/logic/v4`; the legacy `/cloudconfiguration/v1` and
// `/communication/v1` gateways no longer exist.
const NOTIFICATIONS_API_URL = `${baseUrl}/logic/v4`;

/**
 * Listens for live notifications while the user is signed in, and refreshes every `notifications`
 * query when one arrives: the bell's unread dot and, when open, the dropdown list. Call it once, in the
 * layout that is always mounted; the dropdown is unmounted while closed, so a subscription there misses
 * everything sent while it is closed.
 */
export const useNotificationSubscription = () => {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) return;
    let subscription: { stop: () => Promise<void> } | null = null;
    let stopped = false;

    const setup = async () => {
      try {
        const started = await subscribeNotifications(
          NOTIFICATIONS_API_URL, // Goes to fetchNotificationConfigs → /Notification/Gets
          {
            projectKey,
            // SignalR asks for the token on every (re)connect, so it is read from the store each time
            // instead of captured: a token refresh then needs no new subscription. Without one in the
            // store the session cookie authenticates (the configs request is sent with credentials).
            accessTokenFactory: () => useAuthStore.getState().accessToken ?? '',
            signalRBaseUrl: NOTIFICATIONS_API_URL, // Goes to NotificationClientService → /NotificationHub
          },
          () => {
            void queryClient.invalidateQueries({ queryKey: ['notifications'] });
          }
        );
        // Signed out (or unmounted) while connecting: don't leave the connection open.
        if (stopped) void started?.stop?.();
        else subscription = started;
      } catch (error) {
        console.error('Failed to subscribe to notifications:', error);
      }
    };

    void setup();

    return () => {
      stopped = true;
      if (subscription?.stop) void subscription.stop();
    };
  }, [isAuthenticated, queryClient]);
};

/**
 * Hook to fetch notifications with pagination and filtering
 * @param params - Query parameters for filtering and pagination
 * @returns Query result with notifications and metadata
 */
export const useGetNotifications = (params: GetNotificationsParams) => {
  return useQuery({
    queryKey: ['notifications', params],
    queryFn: getNotifications,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to mark a notification as read
 * @returns Mutation function to mark notification as read with loading and error states
 *
 * @example
 * const { mutate: markAsRead, isPending } = useMarkNotificationAsRead();
 *
 * // In your component:
 * markAsRead(notificationId);
 */
export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();

  // No success toast: the notification turning read is the feedback.
  return useMutation({
    mutationFn: markNotificationAsRead,
    onSuccess: (_data, notificationId) => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });

      queryClient.setQueryData<{ notifications: Notification[] }>(['notifications'], (old) => {
        if (!old) return old;

        return {
          ...old,
          notifications: old.notifications.map((notification) =>
            notification.id === notificationId ? { ...notification, isRead: true } : notification
          ),
        };
      });
    },
  });
};

/**
 * Custom hook to mark all notifications as read
 *
 * @param onSuccess - Optional callback function to be called after successful mutation
 * @returns {Object} The mutation object from react-query with the following properties:
 * @property {Function} mutate - Function to trigger the mark all as read action
 * @property {boolean} isPending - Indicates if the mutation is in progress
 * @property {Error | null} error - Error object if the mutation fails
 * @property {boolean} isSuccess - Indicates if the mutation was successful
 * @property {Function} reset - Function to reset the mutation state
 *
 * @example
 * const { mutate: markAllAsRead, isPending } = useMarkAllNotificationAsRead({
 *   onSuccess: () => {
 *     // Handle successful mark all as read
 *   }
 * });
 *
 * // To mark all notifications as read:
 * markAllAsRead();
 */
export const useMarkAllNotificationAsRead = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();

  // No success toast: every notification turning read is the feedback.
  return useMutation({
    mutationFn: markAllNotificationAsRead,
    onSuccess: (data) => {
      queryClient.setQueryData<{ notifications: Notification[] }>(['notifications'], (old) => {
        if (!old) return old;

        return {
          ...old,
          notifications: old.notifications.map((notification) => ({
            ...notification,
            isRead: true,
          })),
        };
      });

      queryClient.invalidateQueries({ queryKey: ['notifications'] });

      if (data.isSuccess) options?.onSuccess?.();
    },
  });
};
